import { useRef, useState } from 'react';
import styles from './css/cclasstrib-import-modal.module.css';
import { importCclasstribTable } from '../../services/cclasstrib/cclasstrib.service';

function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CclasstribImportModal({ isOpen, onClose }) {
    const inputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    if (!isOpen) return null;

    function handleFileChange(e) {
        const selected = e.target.files?.[0] ?? null;
        setFile(selected);
        setResult(null);
        setError(null);
    }

    function handleRemoveFile() {
        setFile(null);
        setResult(null);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
    }

    async function handleImport() {
        if (!file) return;
        setIsLoading(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const data = await importCclasstribTable(formData);

            if (data.erros > 0 && data.cstUpserted === 0 && data.cclassUpserted === 0) {
                throw new Error(data.mensagensErro?.[0] ?? 'Erro ao importar o arquivo.');
            }

            setResult(data);
            setFile(null);
            if (inputRef.current) inputRef.current.value = '';
        } catch (err) {
            setError(err.message ?? 'Erro inesperado ao importar a tabela.');
        } finally {
            setIsLoading(false);
        }
    }

    function handleClose() {
        if (isLoading) return;
        setFile(null);
        setResult(null);
        setError(null);
        if (inputRef.current) inputRef.current.value = '';
        onClose();
    }

    return (
        <div className={styles.overlay} onClick={handleClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

                {/* HEADER */}
                <div className={styles.header}>
                    <h2 className={styles.title}>Importar Tabela de CST/cClassTrib (IBS/CBS)</h2>
                    <button className={styles.closeBtn} onClick={handleClose} disabled={isLoading}>✕</button>
                </div>

                <p className={styles.description}>
                    Selecione o arquivo <strong>.xlsx</strong> oficial publicado pela Receita Federal / Comitê Gestor
                    do IBS (Informe Técnico RFB 2025.002), com as abas de CST e cClassTrib de IBS/CBS.
                    Os códigos já existentes são atualizados (upsert); nada é apagado.
                </p>

                {/* DROPZONE / FILE INPUT */}
                <div
                    className={`${styles.dropzone} ${isLoading ? styles.dropzoneDisabled : ''}`}
                    onClick={() => !isLoading && inputRef.current?.click()}
                >
                    <span className={styles.dropzoneIcon}>📂</span>
                    <p className={styles.dropzoneText}>
                        Clique para selecionar o arquivo <strong>.xlsx</strong>
                    </p>
                    <p className={styles.dropzoneHint}>Máx. 15 MB</p>
                    <input
                        ref={inputRef}
                        type='file'
                        accept='.xlsx'
                        className={styles.hiddenInput}
                        disabled={isLoading}
                        onChange={handleFileChange}
                    />
                </div>

                {/* ARQUIVO SELECIONADO */}
                {file && (
                    <div className={styles.fileItem}>
                        <span className={styles.fileName}>{file.name}</span>
                        <span className={styles.fileSize}>{formatBytes(file.size)}</span>
                        {!isLoading && (
                            <button className={styles.removeBtn} onClick={handleRemoveFile}>✕</button>
                        )}
                    </div>
                )}

                {/* ERRO */}
                {error && (
                    <p className={styles.errorMessage}>{error}</p>
                )}

                {/* RESULTADO */}
                {result && (
                    <div className={styles.resultTable}>
                        <table>
                            <thead>
                                <tr>
                                    {['Arquivo', 'CSTs', 'cClassTrib', 'Erros'].map((col) => (
                                        <th key={col}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={styles.monoCell}>{result.arquivo}</td>
                                    <td className={styles.successCell}>{result.cstUpserted?.toLocaleString('pt-BR') ?? '-'}</td>
                                    <td className={styles.successCell}>{result.cclassUpserted?.toLocaleString('pt-BR') ?? '-'}</td>
                                    <td className={styles.errorCell}>{result.erros?.toLocaleString('pt-BR') ?? '-'}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                {/* FOOTER */}
                <div className={styles.footer}>
                    <button className={styles.cancelBtn} onClick={handleClose} disabled={isLoading}>
                        Fechar
                    </button>
                    <button
                        className={styles.importBtn}
                        onClick={handleImport}
                        disabled={!file || isLoading}
                    >
                        {isLoading ? 'Importando...' : 'Importar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
